import moment from "moment";
import { useQuery } from "@apollo/react-hooks";
import { MY_TIME_GET_ADMISSION } from "my-time/graphql/queries";
import { useMyTimeRequests } from "my-time/hooks";
import { getDateRangeArray, parseDayType } from "my-time/utils";
import { useOcContractCalendar } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type UseMyTimeAdmissionProps = {
  contractCode: string;
  year: number;
  month: number;
  changeMyTimeEnabled: boolean;
  recordingPeriod?: { startDate: string; endDate: string };
  isApproved?: boolean;
};

export const useMyTimeAdmission = ({
  contractCode,
  year,
  month,
  changeMyTimeEnabled,
  recordingPeriod,
  isApproved,
}: UseMyTimeAdmissionProps) => {
  const referenceDate = new Date(`${year}-${month}-15`);
  const isMobile = useOcIsMobile();

  const startDate = moment(referenceDate)
    .startOf("month")
    .subtract(7, "days")
    .toDate();
  const endDate = moment(referenceDate).endOf("month").add(7, "days").toDate();

  const { contractCalendar } = useOcContractCalendar({
    contract: contractCode,
    startDate: moment(referenceDate).startOf("month").toISOString(),
    endDate: moment(referenceDate).endOf("month").toISOString(),
  });

  const { contractCalendar: yearlyCalendar } = useOcContractCalendar({
    contract: contractCode,
    startDate: moment(referenceDate).startOf("year").toISOString(),
    endDate: moment(referenceDate).endOf("year").toISOString(),
  });

  const dates = getDateRangeArray(
    startDate.toISOString(),
    endDate.toISOString(),
  );

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
  } = useMyTimeRequests({
    contractCode,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    requested: false,
    page: 1,
    pageSize: 100,
  });

  const extendedRequestsData = requestsData?.map((request) => {
    if (contractCalendar && yearlyCalendar) {
      const newRequest = { ...request };
      const dayOfYear = moment(request?.startTime).dayOfYear();
      newRequest.date = moment(request?.startTime).toISOString();
      newRequest.title = `${moment(request?.startTime).format(
        "HH:mm",
      )} - ${moment(request.endTime).format("HH:mm")}`;
      newRequest.end = moment(request?.endTime).toDate();
      newRequest.start = moment(request?.startTime).toDate();
      newRequest.dayType = parseDayType(yearlyCalendar[dayOfYear - 1]);
      newRequest.icons = {
        arrow: true,
        add: false,
        edit: false,
      };
      return newRequest;
    }
    return request;
  });

  const { data, loading, error } = useQuery(MY_TIME_GET_ADMISSION, {
    fetchPolicy: "cache-and-network",
    skip: !contractCode,
    variables: {
      contractCode,
      year: String(year),
      month: String(month),
    },
  });

  const admissionsData = data?.getAdmission || [];
  type Admission = {
    date: string | Date;
    startTime: string | Date;
    endTime: string | Date;
    myTimeType: string;
    workBreakInMinutes?: number;
    [key: string]: any;
  };
  const extendedAdmissionsData = admissionsData?.map((admission: Admission) => {
    const newAdmission = { ...admission };
    if (isMobile) {
      newAdmission._id = admission.date;
    }
    newAdmission.date = moment(admission.date).toDate();
    newAdmission.start = moment(admission.startTime).toDate();
    newAdmission.end = moment(admission.endTime).toDate();
    newAdmission.title = `${moment(admission.startTime).format(
      "HH:mm",
    )} - ${moment(admission.endTime).format("HH:mm")}`;
    newAdmission.end = moment(admission.endTime).toDate();
    newAdmission.start = moment(admission.startTime).toDate();
    const icons = {
      arrow: true,
      add: false,
      edit: false,
    };
    if (admission.myTimeType === "WORKING_TIME") {
      icons.arrow = false;
      icons.edit = true;
      icons.add = true;
    }
    if (admission.myTimeType === "HOMEOFFICE") {
      icons.arrow = false;
      icons.edit = false;
      icons.add = true;
    }
    if (admission.myTimeType === "STAND_BY") {
      icons.arrow = false;
      icons.edit = false;
      icons.add = true;
    }
    if (!changeMyTimeEnabled || isApproved) {
      icons.arrow = true;
      icons.edit = false;
      icons.add = false;
    }
    if (recordingPeriod) {
      if (
        moment(admission?.date).format("YYYY-MM-DD") <
        recordingPeriod?.startDate ||
        moment(admission?.date).format("YYYY-MM-DD") > recordingPeriod?.endDate
      ) {
        icons.arrow = true;
        icons.edit = false;
        icons.add = false;
      }
    }

    newAdmission.icons = icons;
    return newAdmission;
  });

  const events: any[] = [];

  dates.forEach((date) => {
    if (contractCalendar && yearlyCalendar) {
      const dayOfYear = moment(date).dayOfYear();
      let icons = {
        arrow: false,
        add: true,
        edit: false,
      };
      if (!changeMyTimeEnabled || isApproved) {
        icons.arrow = true;
        icons.edit = false;
        icons.add = false;
      }
      if (recordingPeriod) {
        if (
          date < recordingPeriod?.startDate ||
          date > recordingPeriod?.endDate
        ) {
          icons.arrow = true;
          icons.edit = false;
          icons.add = false;
        }
      }

      const item = {
        date,
        dayType: parseDayType(yearlyCalendar[dayOfYear - 1]),
        endTime: null,
        startTime: null,
        myTimeType: null,
        itemType: null,
        icons: icons,
        ...(isMobile && { _id: date.toString() }),
      };

      const filteredRequests = extendedRequestsData?.filter(
        (requestItem) =>
          moment(requestItem.date).format("YYYY-MM-DD") ===
          moment(date).format("YYYY-MM-DD") &&
          (requestItem?.state === "approved" ||
            requestItem?.state === "requested"),
      );

      const filteredAdmissions = extendedAdmissionsData?.filter(
        (admissionItem: Admission) =>
          moment(admissionItem.date).format("YYYY-MM-DD") ===
          moment(date).format("YYYY-MM-DD"),
      );

      filteredAdmissions.forEach((admission: Admission) => {
        const isExist = filteredRequests?.find(
          (item) =>
            moment(item.startTime).format("YYYYMMDDHHmm") ===
            moment(admission.startTime).format("YYYYMMDDHHmm") &&
            moment(item.endTime).format("YYYYMMDDHHmm") ===
            moment(admission.endTime).format("YYYYMMDDHHmm") &&
            item.myTimeType === admission.myTimeType,
        );

        const isSameDay = filteredRequests?.find((filterItem) => {
          if (
            moment(filterItem.date).format("YYYY-MM-DD") ===
            moment(admission.date).format("YYYY-MM-DD") &&
            admission.myTimeType === "WORKING_TIME" &&
            filterItem.myTimeType === "WORKING_TIME"
          ) {
            return true;
          }
          return false;
        });
        if (!isExist && !isSameDay) {
          events.push(admission);
        }
      });

      filteredRequests.forEach((request) => {
        const matchingData = extendedAdmissionsData.find(
          (data: Admission) => data.startTime === request.startTime,
        );
        if (matchingData) {
          request.workBreakInMinutes = matchingData.workBreakInMinutes;
        }
        events.push(request);
      });
      if (filteredAdmissions.length === 0 && filteredRequests.length === 0) {
        events.push(item);
      }
    }
  });

  const tableData = events.filter(
    (item) =>
      moment(item.date).format("YYYY-MM-DD") >=
      moment(referenceDate).startOf("month").format("YYYY-MM-DD") &&
      moment(item.date).format("YYYY-MM-DD") <=
      moment(referenceDate).endOf("month").format("YYYY-MM-DD"),
  );

  const filteredEvents = events.filter(
    (event) => event.startTime && event.endTime,
  );

  // Deduplicate events based on unique combination of date, startTime, endTime, and myTimeType
  const deduplicateEvents = (items: any[]) => {
    const seen = new Set<string>();
    return items.filter((item) => {
      const key = `${moment(item.date).format("YYYY-MM-DD")}_${item.startTime}_${item.endTime}_${item.myTimeType}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  // Filter logic: if a day has a LEAVE or HOMEOFFICE type, filter out all other types for that day
  const filterLeaveConflicts = (items: any[]) => {
    // First, deduplicate the items
    const deduplicated = deduplicateEvents(items);

    // Group items by date
    const itemsByDate: Record<string, any[]> = deduplicated.reduce((acc, item) => {
      const dateKey = moment(item.date).format("YYYY-MM-DD");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);
      return acc;
    }, {} as Record<string, any[]>);

    // For each date, check if there's a LEAVE or HOMEOFFICE entry
    const filtered: any[] = [];
    Object.entries(itemsByDate).forEach(([, dateItems]: [string, any[]]) => {
      const hasLeave = dateItems.some((item: any) => item.myTimeType === "LEAVE");
      const hasHomeOffice = dateItems.some((item: any) => item.myTimeType === "HOMEOFFICE");

      if (hasLeave) {
        // Only keep LEAVE entries for this date
        filtered.push(...dateItems.filter((item: any) => item.myTimeType === "LEAVE"));
      } else if (hasHomeOffice) {
        // Only keep HOMEOFFICE entries for this date
        filtered.push(...dateItems.filter((item: any) => item.myTimeType === "HOMEOFFICE"));
      } else {
        // Keep all entries for this date
        filtered.push(...dateItems);
      }
    });

    return filtered;
  };

  const filteredTableData = filterLeaveConflicts(tableData);
  const filteredEventsData = filterLeaveConflicts(filteredEvents);

  return {
    data: filteredTableData || [],
    events: filteredEventsData || [],
    loading: (loading && !data) || (requestsLoading && !requestsData),
    error: error || requestsError,
  };
};

export default useMyTimeAdmission;
